import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '@/components/ui/table';
import {
  Search,
  Eye,
  CheckCircle2,
  Calendar,
  RefreshCw,
  Archive
} from 'lucide-react';
import { toast } from 'sonner';

export const UserHistory = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [total, setTotal] = useState(0);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        status: 'COMPLETED', // history specifically queries completed work
      };

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }

      const res = await api.get('/prescriptions', { params });
      if (res.data) {
        setPrescriptions(res.data.items || []);
        setTotal(res.data.total || 0);
      }
    } catch (err) {
      console.error('Failed to load completed history:', err);
      toast.error('Failed to load completed history');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm, startDate, endDate]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const totalPages = Math.ceil(total / pageSize) || 1;

  const formatDate = (isoString) => {
    if (!isoString) return '—';
    try {
      return new Date(isoString).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return isoString;
    }
  };

  return (
    <div className="space-y-6 pb-8 md:pb-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-start justify-between gap-3 w-full sm:w-auto">
          <div>
            <div className="flex items-center gap-2">
              <Archive className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-700 flex-shrink-0" />
              <h1 className="font-serif text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                My Completed History
              </h1>
            </div>
            <p className="text-slate-600 text-xs sm:text-sm mt-0.5 sm:mt-1">
              Archival record of all prescriptions completed and fulfilled by your desk
            </p>
          </div>
          {/* Mobile Icon-only Refresh Button */}
          <Button
            variant="outline"
            size="icon"
            onClick={fetchHistory}
            disabled={loading}
            className="sm:hidden h-9 w-9 flex-shrink-0"
            aria-label="Refresh history"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Desktop Refresh Button */}
        <Button
          variant="outline"
          onClick={fetchHistory}
          disabled={loading}
          className="hidden sm:inline-flex gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filter and Search Bar */}
      <Card className="shadow-sm border-slate-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search patient name, phone, or prescription ID..."
                className="pl-9 h-10 border-slate-300"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div>
              <Input
                type="date"
                className="h-10 border-slate-300"
                placeholder="From date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div>
              <Input
                type="date"
                className="h-10 border-slate-300"
                placeholder="To date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="shadow-sm border-slate-200">
        <CardHeader className="border-b border-slate-100 py-4 px-6 flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <CardTitle className="text-base font-semibold text-slate-800">
              Completed Prescriptions ({total})
            </CardTitle>
          </div>
          <span className="text-xs text-slate-500">
            Page {page} of {totalPages}
          </span>
        </CardHeader>
        <CardContent className="p-0">
          {/* Mobile Card List (< md) */}
          <div className="block md:hidden divide-y divide-slate-100">
            {loading ? (
              <div className="text-center py-12 text-slate-500">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto text-emerald-600 mb-2" />
                Loading history...
              </div>
            ) : prescriptions.length > 0 ? (
              prescriptions.map((rx) => (
                <div key={rx.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900 text-base">
                          {rx.patient_name || rx.patientName || 'Patient'}
                        </span>
                        <span className="font-mono text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                          #{rx.id}
                        </span>
                      </div>
                      {rx.phone && (
                        <div className="text-xs text-slate-500 mt-0.5">{rx.phone}</div>
                      )}
                    </div>
                    <Badge variant="outline" className="text-xs flex-shrink-0">
                      {rx.type || 'Digital'}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 bg-slate-50 p-2.5 rounded-lg">
                    <div>
                      <span className="text-slate-400 block text-[11px]">Visit Date</span>
                      <span className="font-medium text-slate-700">{rx.visit_date || rx.date || '—'}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[11px]">Completed At</span>
                      <span className="font-medium text-emerald-700">{formatDate(rx.completed_at)}</span>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    className="w-full h-11 text-sm font-medium gap-2 text-slate-700 hover:text-slate-900 border-slate-300"
                    onClick={() => navigate(`/user/prescriptions/${rx.id}`)}
                  >
                    <Eye className="h-4 w-4" />
                    Review Timeline & Details
                  </Button>
                </div>
              ))
            ) : (
              <div className="text-center py-12 text-slate-500 text-sm">
                No completed prescriptions match your criteria.
              </div>
            )}
          </div>

          {/* Desktop Table (md+) */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="w-16">ID</TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead>Visit Date</TableHead>
                  <TableHead>Started At</TableHead>
                  <TableHead>Completed At</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto text-emerald-600 mb-2" />
                      Loading history...
                    </TableCell>
                  </TableRow>
                ) : prescriptions.length > 0 ? (
                  prescriptions.map((rx) => (
                    <TableRow key={rx.id} className="hover:bg-slate-50/75">
                      <TableCell className="font-mono text-xs text-slate-500">
                        #{rx.id}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-900">
                          {rx.patient_name || rx.patientName || 'Patient'}
                        </div>
                        {rx.phone && (
                          <div className="text-xs text-slate-500">{rx.phone}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {rx.visit_date || rx.date || '—'}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {formatDate(rx.started_at)}
                      </TableCell>
                      <TableCell className="text-xs font-medium text-emerald-700">
                        {formatDate(rx.completed_at)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {rx.type || 'Digital'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 gap-1 text-slate-600 hover:text-slate-900"
                          onClick={() => navigate(`/user/prescriptions/${rx.id}`)}
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Review Timeline
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      No completed prescriptions match your criteria.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-100">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <span className="text-xs text-slate-600">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserHistory;
